// src/App.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Login from './components/auth/login';
import ForgotPassword from './components/auth/ForgotPassword';
import SetupPassword from './components/auth/SetupPassword';

// Agent Components
import DeliverBills from './components/agent/DeliverBills';
import PropertyDetails from './components/agent/PropertyDetails';
// Admin Components
import AdminDashboard from './components/admin/AdminDashboard';
import CreateUser from './components/admin/CreateUser';
import AssignBills from './components/admin/AssignBills';
import AgentDashboard from './components/agent/AgentDashboard';
import AgentPropertiesMap from './components/agent/AgentPropertiesMap';
import AgentPerformance from './components/agent/AgentPerformance';
import BillsManagement from './components/admin/BillsManagement';
import DeliveryForm from './components/agent/DeliveryForm';
import AssignProperties from './components/admin/AssignProperties';
import AgentManagement from './components/admin/AgentManagement';
import ReportsHub from './components/reports/ReportsHub';
import ImportPayments from './components/admin/ImportPayments';
import SupervisorManagement from './components/admin/SupervisorManagement';
import AssignAgentsToSupervisor from './components/admin/AssignAgentsToSupervisor';
import SupervisorDashboard from './components/supervisor/SupervisorDashboard';
import SupervisorAgentActivity from './components/supervisor/SupervisorAgentActivity';
import ExternalCollectionUnits from './components/external/ExternalCollectionUnits';
import ExternalUnitProperties from './components/external/ExternalUnitProperties';
import ExternalPropertyDetail from './components/external/ExternalPropertyDetail';

// Create Context for backward compatibility
export const AppContext = createContext();

// Wrapper App Component
export default function App() {
	const location = useLocation();
	const isLaptopShellRoute =
		location.pathname === '/admin-reports' ||
		location.pathname === '/external/reports';

	return (
		<div
			className={
				isLaptopShellRoute
					? 'min-h-[100dvh] w-full bg-[#000B18]'
					: 'min-h-[100dvh] w-full bg-[#000B18] lg:flex lg:items-center lg:justify-center lg:bg-gradient-to-br lg:from-[#000B18] lg:to-[#001E3C] lg:p-4'
			}>
			<div
				className={
				isLaptopShellRoute
						? 'flex w-full h-[100dvh] flex-col overflow-hidden bg-[#000B18]'
					: 'flex w-full flex-col min-h-0 overflow-x-hidden overflow-y-auto bg-[#000B18] shadow-2xl h-[100dvh] lg:max-w-md lg:border lg:border-white/10 lg:rounded-[2rem] lg:h-[844px]'
				}>
				<AppContent />
			</div>
		</div>
	);
}

// Main App Logic with Routing
function AppContent() {
	const auth = useAuth();
	const { user, isAuthenticated, isAdmin, isSupervisor, logout, refreshAgentTreatment } = auth;
	const navigate = useNavigate();

	// === PROTECTED ROUTE HELPER ===
	const ProtectedRoute = ({ children, allowedRoles }) => {
		if (!isAuthenticated || auth.loading) {
			return <Navigate to='/' />;
		}
		const role = user?.userType;
		if (!allowedRoles.includes(role)) {
			if (role === 'admin') return <Navigate to='/admin/dashboard' />;
			if (role === 'supervisor') return <Navigate to='/supervisor/dashboard' />;
			if (role === 'external_user') return <Navigate to='/external/collection-units' />;
			return <Navigate to='/agent/dashboard' />;
		}
		return children;
	};

	// Treatment 1 agents cannot access Properties Map. Refetch treatment once so upgraded agents get in without re-login.
	const AgentTreatment2Only = ({ children }) => {
		const [refetchDone, setRefetchDone] = useState(false);
		const isAgent = user?.userType === 'agent';
		const isT1 = user?.treatmentCategory === 'TREATMENT_1';

		useEffect(() => {
			if (isAgent && isT1 && !refetchDone && refreshAgentTreatment) {
				refreshAgentTreatment().then(() => setRefetchDone(true));
			}
			// refreshAgentTreatment from AuthContext is stable; listing it triggers react-hooks/exhaustive-deps false positive
			// eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run when agent/treatment gate changes
		}, [isAgent, isT1, refetchDone]);

		if (isAgent && isT1) {
			if (!refetchDone) return null; // brief wait while refetching treatment
			return <Navigate to='/agent/dashboard' />;
		}
		return children;
	};

	// Context value for backward compatibility
	const contextValue = {
		currentUser: user,
		isAdmin: isAdmin,
		logout: logout,
		navigate: (path) => {
			navigate(path);
		},
	};

	return (
		<AppContext.Provider value={contextValue}>
			<div className="flex-1 flex flex-col min-h-0 overflow-x-hidden overflow-y-auto">
			<Routes>
				{/* Public */}
				<Route
					path='/'
					element={isAuthenticated ? <Navigate to={isAdmin ? '/admin/dashboard' : isSupervisor ? '/supervisor/dashboard' : user?.userType === 'external_user' ? '/external/collection-units' : '/agent/dashboard'} /> : <Login />}
				/>

				<Route
					path='/forgot-password'
					element={<ForgotPassword />}
				/>

				<Route
					path='/setup-password'
					element={<SetupPassword />}
				/>

				{/* Agent Routes */}
				<Route
					path='/agent/dashboard'
					element={
						<ProtectedRoute allowedRoles={['agent']}>
							<AgentDashboard />
						</ProtectedRoute>
					}
				/>
				<Route
					path='/agent/data-collection-map'
					element={
						<ProtectedRoute allowedRoles={['agent']}>
							<Navigate to='/agent/dashboard' replace />
						</ProtectedRoute>
					}
				/>
				<Route
					path='/agent/data-collection-form'
					element={
						<ProtectedRoute allowedRoles={['agent']}>
							<Navigate to='/agent/dashboard' replace />
						</ProtectedRoute>
					}
				/>
				<Route
					path='/agent/deliver-bills'
					element={
						<ProtectedRoute allowedRoles={['agent']}>
							<DeliverBills />
						</ProtectedRoute>
					}
				/>
				<Route
					path='/agent/property/:billNo'
					element={
						<ProtectedRoute allowedRoles={['agent']}>
							<PropertyDetails />
						</ProtectedRoute>
					}
				/>
				<Route
					path='/agent/delivery-form/:billNo'
					element={
						<ProtectedRoute allowedRoles={['agent']}>
							<DeliveryFormWrapper />
						</ProtectedRoute>
					}
				/>
				<Route
					path='/agent/properties-map'
					element={
						<ProtectedRoute allowedRoles={['agent']}>
							<AgentTreatment2Only><AgentPropertiesMap /></AgentTreatment2Only>
						</ProtectedRoute>
					}
				/>
				<Route
					path='/agent/performance'
					element={
						<ProtectedRoute allowedRoles={['agent']}>
							<AgentPerformance />
						</ProtectedRoute>
					}
				/>
				<Route
					path='/agent/reports'
					element={
						<ProtectedRoute allowedRoles={['agent']}>
							<ReportsHub />
						</ProtectedRoute>
					}
				/>

				{/* Admin Routes */}
				<Route
					path='/admin/dashboard'
					element={
						<ProtectedRoute allowedRoles={['admin']}>
							<AdminDashboard />
						</ProtectedRoute>
					}
				/>
				<Route
					path='/admin/create-user'
					element={
						<ProtectedRoute allowedRoles={['admin']}>
							<CreateUser />
						</ProtectedRoute>
					}
				/>
				<Route path='/admin/create-agent' element={<Navigate to='/admin/create-user' replace />} />
				<Route path='/admin/create-supervisor' element={<Navigate to='/admin/create-user' replace />} />
				<Route
					path='/admin/assign-bills'
					element={
						<ProtectedRoute allowedRoles={['admin']}>
							<AssignBills />
						</ProtectedRoute>
					}
				/>
				<Route
					path='/admin/assign-properties/:agentId'
					element={
						<ProtectedRoute allowedRoles={['admin']}>
							<AssignPropertiesWrapper />
						</ProtectedRoute>
					}
				/>
				<Route
					path='/admin/bills-management'
					element={
						<ProtectedRoute allowedRoles={['admin']}>
							<BillsManagement />
						</ProtectedRoute>
					}
				/>
				<Route
					path='/admin/user-management'
					element={
						<ProtectedRoute allowedRoles={['admin']}>
							<AgentManagement />
						</ProtectedRoute>
					}
				/>
				<Route
					path='/admin-reports'
					element={
						<ProtectedRoute allowedRoles={['admin']}>
							<ReportsHub />
						</ProtectedRoute>
					}
				/>
				<Route path='/admin/import-payments' element={<ProtectedRoute allowedRoles={['admin']}><ImportPayments /></ProtectedRoute>} />
				<Route path='/admin/supervisor-management' element={<ProtectedRoute allowedRoles={['admin']}><SupervisorManagement /></ProtectedRoute>} />
				<Route path='/admin/assign-agents/:supervisorId' element={<ProtectedRoute allowedRoles={['admin']}><AssignAgentsToSupervisor /></ProtectedRoute>} />

				{/* External viewer (read-only) */}
				<Route path='/external/collection-units' element={<ProtectedRoute allowedRoles={['external_user']}><ExternalCollectionUnits /></ProtectedRoute>} />
				<Route path='/external/collection-units/:unit' element={<ProtectedRoute allowedRoles={['external_user']}><ExternalUnitProperties /></ProtectedRoute>} />
				<Route path='/external/property/:id' element={<ProtectedRoute allowedRoles={['external_user']}><ExternalPropertyDetail /></ProtectedRoute>} />

				{/* Supervisor Routes */}
				<Route path='/supervisor/dashboard' element={<ProtectedRoute allowedRoles={['supervisor']}><SupervisorDashboard /></ProtectedRoute>} />
				<Route path='/supervisor/reports' element={<ProtectedRoute allowedRoles={['supervisor']}><ReportsHub /></ProtectedRoute>} />
				<Route path='/supervisor/agent/:agentId' element={<ProtectedRoute allowedRoles={['supervisor']}><SupervisorAgentActivity /></ProtectedRoute>} />

				{/* Fallback */}
				<Route
					path='*'
					element={<Navigate to='/' />}
				/>
			</Routes>
			</div>
		</AppContext.Provider>
	);
}

// === WRAPPERS TO PASS CONTEXT + URL PARAMS ===
const DeliveryFormWrapper = () => {
	const { currentUser } = useContext(AppContext);
	const billNo = window.location.pathname.split('/').pop();
	return (
		<DeliveryForm
			billNo={billNo}
			agentId={currentUser?.id}
		/>
	);
};

const AssignPropertiesWrapper = () => {
	const agentId = window.location.pathname.split('/').pop();
	return (
		<AssignProperties
			agentId={agentId}
		/>
	);
};





